// LUMINA · pages/vajra/PractitionerDashboard.jsx
// Phase 113: Practitioner personal centre — registrations, offerings, service requests

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API, GD, GDH, BG, TXT, TXT2 } from './constants';

const STATUS_COLORS = {
  pending: "#d97706", assigned: "#2563eb", in_progress: "#7c3aed",
  completed: "#059669", cancelled: "#9ca3af",
  accepted: "#2563eb", declined: "#9ca3af",
};

export default function PractitionerDashboard({ lang: langProp }) {
  const lang = langProp || localStorage.getItem("lumina_lang") || "zh";
  const L = (zh, en) => lang === "zh" ? zh : en;
  const navigate = useNavigate();

  const [tab, setTab] = useState("registrations");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Try multiple token keys (Vajra portal uses vajra_token; LUMINA uses lumina_token)
    const token = localStorage.getItem("vajra_token") || localStorage.getItem("lumina_token") || localStorage.getItem("token");
    if (!token) { navigate("/vajra"); return; }
    fetch(`${API}/vajra/my-dashboard`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        if (r.status === 401) throw new Error("401");
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(d => { setData(d); setLoading(false); })
      .catch(e => {
        if (e.message === "401") navigate("/vajra");
        else { setError(e.message); setLoading(false); }
      });
  }, []);

  const TABS = [
    { key: "registrations", zh: "我的报名", en: "Registrations" },
    { key: "offerings", zh: "我的供养", en: "Offerings" },
    { key: "requests", zh: "服务请求", en: "Requests" },
  ];

  if (loading) return (
    <div style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center", background: BG }}>
      <div style={{ color: TXT2, fontSize: 14 }}>{L("加载中…", "Loading…")}</div>
    </div>
  );

  if (error) return (
    <div style={{ minHeight: "60vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: BG, gap: 16 }}>
      <div style={{ color: "#dc2626", fontSize: 14 }}>{L("加载失败：", "Load error: ")}{error}</div>
      <button onClick={() => navigate("/vajra")} style={{ background: GD, color: "#fff", border: "none", borderRadius: 8, padding: "10px 24px", cursor: "pointer" }}>
        {L("返回金刚社区", "Back to Portal")}
      </button>
    </div>
  );

  const member = data?.member || {};

  return (
    <div style={{ minHeight: "100vh", background: BG, fontFamily: "'Noto Serif SC','Noto Serif',serif", color: TXT, paddingBottom: 80 }}>
      {/* Header */}
      <div style={{ background: "linear-gradient(135deg,rgba(212,175,55,0.12),rgba(212,175,55,0.05))", borderBottom: "1px solid rgba(212,175,55,0.2)", padding: "32px 24px 24px" }}>
        <div style={{ maxWidth: 800, margin: "0 auto" }}>
          <button onClick={() => navigate("/vajra")} style={{ background: "none", border: "none", color: GD, cursor: "pointer", fontSize: 13, marginBottom: 16, padding: 0 }}>
            ← {L("金刚社区", "Vajra Portal")}
          </button>
          <div style={{ fontSize: 22, fontWeight: 600, color: TXT }}>{L("修行者中心", "Practitioner Centre")}</div>
          {member.dharma_name && (
            <div style={{ fontSize: 14, color: TXT2, marginTop: 4 }}>{member.dharma_name}</div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ maxWidth: 800, margin: "0 auto", padding: "0 24px" }}>
        <div style={{ display: "flex", borderBottom: "1px solid rgba(212,175,55,0.2)", marginTop: 24 }}>
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} style={{
              padding: "10px 20px", background: "none", border: "none",
              borderBottom: `2px solid ${tab === t.key ? GDH : "transparent"}`,
              color: tab === t.key ? GD : TXT2, cursor: "pointer", fontSize: 13, letterSpacing: 0.5,
            }}>
              {L(t.zh, t.en)}
            </button>
          ))}
        </div>

        <div style={{ paddingTop: 24 }}>
          {tab === "registrations" && <RegistrationsTab items={data?.registrations || []} L={L} />}
          {tab === "offerings" && <OfferingsTab items={data?.offerings || []} L={L} />}
          {tab === "requests" && <RequestsTab items={data?.service_requests || []} L={L} />}
        </div>
      </div>
    </div>
  );
}

function RegistrationsTab({ items, L }) {
  if (!items.length) return <EmptyState msg={L("暂无报名记录", "No registrations yet")} />;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {items.map(r => (
        <div key={r.id} style={{ background: "#fff", border: "1px solid rgba(0,0,0,0.08)", borderRadius: 10, padding: "16px 20px" }}>
          <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 4 }}>{r.event_title || `Event #${r.event_id}`}</div>
          {r.event_date && <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 6 }}>{r.event_date}</div>}
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <StatusBadge status={r.status} />
            {r.payment_status && <StatusBadge status={r.payment_status} prefix={L("付款:", "Payment:")} />}
          </div>
        </div>
      ))}
    </div>
  );
}

function OfferingsTab({ items, L }) {
  if (!items.length) return <EmptyState msg={L("暂无供养记录", "No offerings yet")} />;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {items.map(o => (
        <div key={o.id} style={{ background: "#fff", border: "1px solid rgba(0,0,0,0.08)", borderRadius: 10, padding: "16px 20px" }}>
          <div style={{ fontWeight: 600, fontSize: 15 }}>
            {o.amount_cad_cents != null ? `CAD $${(o.amount_cad_cents / 100).toFixed(2)}` : "—"}
          </div>
          {o.note && <div style={{ fontSize: 13, color: "#6b7280", marginTop: 4 }}>{o.note}</div>}
          <div style={{ marginTop: 8 }}><StatusBadge status={o.status || "completed"} /></div>
        </div>
      ))}
    </div>
  );
}

function RequestsTab({ items, L }) {
  if (!items.length) return <EmptyState msg={L("暂无服务请求", "No service requests yet")} />;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {items.map(r => (
        <div key={r.id} style={{ background: "#fff", border: "1px solid rgba(0,0,0,0.08)", borderRadius: 10, padding: "16px 20px" }}>
          <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 6 }}>{r.description || `Request #${r.id}`}</div>
          <StatusBadge status={r.status} />
        </div>
      ))}
    </div>
  );
}

function StatusBadge({ status, prefix = "" }) {
  const color = STATUS_COLORS[status] || "#6b7280";
  return (
    <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 4, background: color + "20", color, border: `1px solid ${color}40` }}>
      {prefix}{status}
    </span>
  );
}

function EmptyState({ msg }) {
  return <div style={{ textAlign: "center", padding: "48px 0", color: "#9ca3af", fontSize: 13 }}>{msg}</div>;
}
